import {useState, useEffect, useRef} from "preact/hooks";

interface TooltipProps {
    cssClasses?: string;
    content?: any;
    children?: preact.ComponentChildren;
}

export function Tooltip({ cssClasses, content, children }: TooltipProps) {
    const [open, setOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null)
    const tooltipRef = useRef<HTMLDivElement>(null)
    const [tooltipStyle, setTooltipStyle] = useState<{maxWidth?: string, left?: string, transform?: string}>({})
    const [arrowStyle, setArrowStyle] = useState<{left?: string}>({})

    const toggleTooltip = () => setOpen((prev) => !prev);

    useEffect(() => {
        const handleClickOutside = (e: Event) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };

        const shadowRoot = containerRef.current?.getRootNode() as ShadowRoot | Document;

        if (shadowRoot && shadowRoot !== document) {
            shadowRoot.addEventListener("mousedown", handleClickOutside);
            return () => shadowRoot.removeEventListener("mousedown", handleClickOutside);
        } else {
            document.addEventListener("mousedown", handleClickOutside);
            return () => document.removeEventListener("mousedown", handleClickOutside);
        }
    }, []);

    // Adjust tooltip position and arrow to stay within viewport with 10px padding
    useEffect(() => {
        if (open && tooltipRef.current && containerRef.current) {
            const tooltip = tooltipRef.current;
            const container = containerRef.current;
            const containerRect = container.getBoundingClientRect();
            const tooltipRect = tooltip.getBoundingClientRect();
            const viewportWidth = window.innerWidth;
            const padding = 10;

            // Start with centered position
            let left = '50%';
            let transform = 'translateX(-50%)';
            let maxWidth: string | undefined = undefined;
            let arrowLeft = '50%'; // Default: arrow centered in tooltip

            // Calculate if tooltip would overflow
            const centerX = containerRect.left + (containerRect.width / 2);
            const tooltipHalfWidth = tooltipRect.width / 2;

            const leftEdge = centerX - tooltipHalfWidth;
            const rightEdge = centerX + tooltipHalfWidth;

            // Check if tooltip overflows left edge
            if (leftEdge < padding) {
                const tooltipLeft = padding - containerRect.left;
                left = `${tooltipLeft}px`;
                transform = 'none';

                // Calculate arrow position: center of icon relative to tooltip
                const iconCenterRelativeToTooltip = (containerRect.width / 2) - tooltipLeft;
                arrowLeft = `${iconCenterRelativeToTooltip}px`;
            }
            // Check if tooltip overflows right edge
            else if (rightEdge > viewportWidth - padding) {
                const tooltipLeft = viewportWidth - padding - containerRect.left - tooltipRect.width;
                left = `${tooltipLeft}px`;
                transform = 'none';

                // Calculate arrow position: center of icon relative to tooltip
                const iconCenterRelativeToTooltip = (containerRect.width / 2) - tooltipLeft;
                arrowLeft = `${iconCenterRelativeToTooltip}px`;
            }

            // Set max width if needed
            const maxAllowedWidth = viewportWidth - (2 * padding);
            if (tooltipRect.width > maxAllowedWidth) {
                maxWidth = `${maxAllowedWidth}px`;
            }

            setTooltipStyle({ left, transform, maxWidth });
            setArrowStyle({ left: arrowLeft });
        }
    }, [open]);

    return (
        <span className={`tool-tip relative font-montserrat font-normal text-12px ${cssClasses ?? ''}`} ref={containerRef}>
            <button onClick={toggleTooltip} className="cursor-pointer">{children ?? 'tooltip'}</button>
            {open && (
                <div
                    ref={tooltipRef}
                    onClick={() => setOpen(false)}
                    className={`
                         min-w-52 py-4 px-4 z-60 drop-shadow-sm
                        rounded-10px bg-theme-gray-medium
                        text-theme-black
                        absolute
                        top-full
                        mt-[10px]
                        leading-[1.5em]
                        before:content-['']
                        before:absolute
                        before:bottom-full
                        before:-translate-x-1/2
                        before:inline-block
                        before:w-0 before:h-0
                        before:border-l-[7px] before:border-l-transparent
                        before:border-b-[10px] before:border-b-theme-gray-medium
                        before:border-r-[7px] before:border-r-transparent
                        before:z-10
                        ${(!content || content === '') ? 'hidden' : ''}
                    `}
                    dangerouslySetInnerHTML={{__html: content}}
                    style={{
                        ...tooltipStyle,
                        ['--arrow-left' as any]: arrowStyle.left
                    } as any}
                ></div>
            )}
            <style>{`
                .tool-tip > div::before {
                    left: var(--arrow-left, 50%);
                }
            `}</style>
        </span>
    );
}
